import { Theme } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { buildChatPanelConfig } from '../config/boost-ai';
import { BoostAuthenticationClient } from '../services/boostAuthentication';

const useBoostAI = (theme: Theme, authenticateCallback: (isAuthenticated: boolean) => void) => {
	const isInitialized = useRef(false);
	const boostTenant = import.meta.env.VITE_BOOST_AI_TENANT || 'acorn-insurance';
	const boostFilter = import.meta.env.VITE_BOOST_AI_FILTER || 'q_Chat_Main';

	const handleAuthCallback = useCallback(
		(code: string) => {
			// Use the authorization code to get a one-time code from Boost
			if (import.meta.env.VITE_BOOST_AI_USE_AUTH === 'true') {
				const boostAuthenticationClient = new BoostAuthenticationClient();

				boostAuthenticationClient
					.getBoostOneTimeCode({
						code: code,
						filter_values: [boostFilter],
						redirect_uri: `${window.location.origin}/silent-callback.html`,
					})
					.then((response) => {
						console.log('Boost authentication successful:', response);

						// Initialize chat with the one-time code
						if (window.boost?.chatPanel) {
							window.boost.chatPanel.loginEvent({
								authType: 'openid',
								conversation_id: response.conversation_id,
								authContent: response.one_time_code,
							});

							window.boost.chatPanel.setConversationId(response.conversation_id);

							// Show the chat panel after successful authentication
							window.boost.chatPanel.show();
						}
					})
					.catch((error) => {
						console.error('Error authenticating with Boost:', error);
					});
			}
		},

		[boostFilter],
	);

	const initialiseChat = useCallback(() => {
		// Prevent multiple initializations
		if (isInitialized.current || window.boost) {
			return;
		}

		if (window.boostInit !== undefined) {
			isInitialized.current = true;

			window.boost = window.boostInit(boostTenant, {
				chatPanel: buildChatPanelConfig(theme, boostFilter),
			});

			window.boost.chatPanel.addEventListener('authStatusChanged', (event) => {
				const isAuthenticated = !!event.detail?.authenticatedUserId;
				if (!isAuthenticated) authenticateCallback(false);
			});
		}
	}, [boostTenant, boostFilter, theme, authenticateCallback]);

	// Initialize once when the hook is first used

	useEffect(() => {
		if (!isInitialized.current) initialiseChat();
	}, [initialiseChat]);

	return {
		isInitialized: isInitialized.current,
		handleAuthCallback,
	};
};

export default useBoostAI;
